/** SSO admin editor — Phase 3-sso + Phase 3-saml admin.
 *
 * Lets the org admin configure the org's IdP (OIDC or SAML) without
 * needing engineering involvement. Form mirrors the OrgIdpConfig
 * shape exactly; one "Save" mutation hits PUT /v1/orgs/me/idp.
 *
 * The provider selector at the top swaps the bottom of the form
 * between OIDC fields (issuer URL + client id/secret + discovery probe)
 * and SAML fields (entity ID + SSO URL + x509 cert + attribute mapping).
 *
 * Surfaces the platform-side callback / ACS URL prominently so admins
 * know what to paste into their IdP. The URL is derived from
 * `window.location.origin` since the backend computes it the same way
 * at runtime.
 *
 * Secret-handling pattern (both providers): the stored credential is
 * never echoed back; the read shape carries `has_client_secret` /
 * `has_x509_cert` booleans. An empty value on PUT means "leave the
 * stored credential alone" — admins can edit `email_domains` without
 * re-typing the secret each time.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { PasswordInput } from "@/components/PasswordInput";
import { ApiPaths, api } from "@/lib/api";
import type {
  IdpDiscoveryProbeResult,
  OrgIdpConfig,
  OrgIdpConfigInput,
} from "@/lib/types";

type ProviderKey = "oidc" | "saml";

type FormState = {
  provider: ProviderKey;
  display_name: string;
  // OIDC
  issuer_url: string;
  client_id: string;
  client_secret: string;
  // SAML
  saml_entity_id: string;
  saml_sso_url: string;
  saml_x509_cert: string;
  saml_name_id_format: string;
  saml_attribute_email: string;
  saml_attribute_first_name: string;
  saml_attribute_last_name: string;
  // Shared
  email_domains: string;
  auto_provision_users: boolean;
  default_role_slug: string;
  enabled: boolean;
};

const SAML_DEFAULTS = {
  name_id_format: "urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress",
  attr_email:
    "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress",
  attr_first_name:
    "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname",
  attr_last_name:
    "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname",
} as const;

const EMPTY: FormState = {
  provider: "oidc",
  display_name: "",
  issuer_url: "",
  client_id: "",
  client_secret: "",
  saml_entity_id: "",
  saml_sso_url: "",
  saml_x509_cert: "",
  saml_name_id_format: SAML_DEFAULTS.name_id_format,
  saml_attribute_email: SAML_DEFAULTS.attr_email,
  saml_attribute_first_name: SAML_DEFAULTS.attr_first_name,
  saml_attribute_last_name: SAML_DEFAULTS.attr_last_name,
  email_domains: "",
  auto_provision_users: true,
  default_role_slug: "inspector",
  enabled: true,
};

export function SsoConfigCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [probe, setProbe] = useState<IdpDiscoveryProbeResult | null>(null);

  const config = useQuery({
    queryKey: ["org-idp"],
    queryFn: async () => {
      const res = await api.get<OrgIdpConfig>(ApiPaths.orgIdpConfig);
      return res.data;
    },
    retry: (failureCount, err) => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      // 404 just means "no config yet" — don't retry.
      if (status === 404) return false;
      return failureCount < 2;
    },
  });

  // Seed the form once when the existing config arrives (or stays
  // empty if 404). We deliberately don't re-sync on every refetch —
  // that would clobber user edits.
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (seeded) return;
    if (config.data) {
      const provider =
        config.data.provider === "saml" ? "saml" : "oidc";
      setForm({
        provider,
        display_name: config.data.display_name,
        // OIDC fields — keep whatever's there; UI hides when provider=saml.
        issuer_url: config.data.issuer_url || "",
        client_id: config.data.client_id || "",
        client_secret: "", // server never echoes the secret
        // SAML fields — same treatment.
        saml_entity_id: config.data.saml_entity_id || "",
        saml_sso_url: config.data.saml_sso_url || "",
        saml_x509_cert: "", // server never echoes the cert
        saml_name_id_format:
          config.data.saml_name_id_format || SAML_DEFAULTS.name_id_format,
        saml_attribute_email:
          config.data.saml_attribute_email || SAML_DEFAULTS.attr_email,
        saml_attribute_first_name:
          config.data.saml_attribute_first_name ||
          SAML_DEFAULTS.attr_first_name,
        saml_attribute_last_name:
          config.data.saml_attribute_last_name || SAML_DEFAULTS.attr_last_name,
        email_domains: config.data.email_domains.join(", "),
        auto_provision_users: config.data.auto_provision_users,
        default_role_slug: config.data.default_role_slug,
        enabled: config.data.enabled,
      });
      setSeeded(true);
    } else if (config.isFetched && !config.data) {
      setSeeded(true);
    }
  }, [config.data, config.isFetched, seeded]);

  const save = useMutation({
    mutationFn: async (input: OrgIdpConfigInput) => {
      const res = await api.put<OrgIdpConfig>(ApiPaths.orgIdpConfig, input);
      return res.data;
    },
    onSuccess: (next) => {
      qc.setQueryData(["org-idp"], next);
    },
  });

  const remove = useMutation({
    mutationFn: async () => {
      await api.delete(ApiPaths.orgIdpConfig);
    },
    onSuccess: () => {
      qc.setQueryData(["org-idp"], undefined);
      qc.removeQueries({ queryKey: ["org-idp"] });
      setForm(EMPTY);
      setSeeded(true);
    },
  });

  const testDiscovery = useMutation({
    mutationFn: async () => {
      const res = await api.post<IdpDiscoveryProbeResult>(
        ApiPaths.orgIdpTestDiscovery,
        { issuer_url: form.issuer_url },
      );
      return res.data;
    },
    onSuccess: setProbe,
  });

  // Phase 3-saml-metadata — drop a SAML metadata.xml file; backend
  // parses + returns {entity_id, sso_url, x509_cert}. We auto-fill
  // the matching form fields; admin still confirms via the Save button.
  const [metadataMessage, setMetadataMessage] = useState<string>("");
  const importMetadata = useMutation({
    mutationFn: async (xml: string) => {
      const res = await api.post<{
        ok: boolean;
        reason?: string;
        detail?: string;
        saml_entity_id?: string;
        saml_sso_url?: string;
        saml_x509_cert?: string;
      }>(ApiPaths.orgIdpImportSamlMetadata, { metadata_xml: xml });
      return res.data;
    },
    onSuccess: (data) => {
      if (!data.ok) {
        setMetadataMessage(
          data.detail || `Couldn't parse metadata (${data.reason || "unknown"})`,
        );
        return;
      }
      setForm((prev) => ({
        ...prev,
        provider: "saml",
        saml_entity_id: data.saml_entity_id || prev.saml_entity_id,
        saml_sso_url: data.saml_sso_url || prev.saml_sso_url,
        // The cert is base64 (no headers). Show it in the textarea so
        // admins can review before saving.
        saml_x509_cert: data.saml_x509_cert || prev.saml_x509_cert,
      }));
      setMetadataMessage(t("admin.sso.metadataLoaded"));
    },
    onError: () => {
      setMetadataMessage(t("admin.sso.metadataUploadFailed"));
    },
  });

  // Both protocols round-trip a URL the IdP needs to know about. OIDC
  // calls it a redirect URI; SAML calls it the Assertion Consumer
  // Service URL. The platform serves them at separate paths.
  const baseOrigin = window.location.origin.replace(
    /^https?:\/\//,
    "https://",
  );
  const oidcCallbackUrl = `${baseOrigin}/api/v1/auth/sso/callback`;
  const samlAcsUrl = `${baseOrigin}/api/v1/auth/saml/acs`;
  const callbackUrl =
    form.provider === "saml" ? samlAcsUrl : oidcCallbackUrl;
  const callbackLabel =
    form.provider === "saml"
      ? t("admin.sso.acsUrlLabel")
      : t("admin.sso.callbackUrlLabel");

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Shared fields go in every payload regardless of provider.
    const payload: OrgIdpConfigInput = {
      provider: form.provider,
      display_name: form.display_name.trim(),
      email_domains: form.email_domains
        .split(",")
        .map((d) => d.trim())
        .filter(Boolean),
      auto_provision_users: form.auto_provision_users,
      default_role_slug: form.default_role_slug.trim() || "inspector",
      enabled: form.enabled,
    };
    if (form.provider === "oidc") {
      payload.issuer_url = form.issuer_url.trim();
      payload.client_id = form.client_id.trim();
      if (form.client_secret) payload.client_secret = form.client_secret;
    } else {
      payload.saml_entity_id = form.saml_entity_id.trim();
      payload.saml_sso_url = form.saml_sso_url.trim();
      if (form.saml_x509_cert) payload.saml_x509_cert = form.saml_x509_cert;
      // Send the attribute mapping only when the admin has typed
      // something — empty values let the server keep its defaults.
      if (form.saml_name_id_format)
        payload.saml_name_id_format = form.saml_name_id_format.trim();
      if (form.saml_attribute_email)
        payload.saml_attribute_email = form.saml_attribute_email.trim();
      if (form.saml_attribute_first_name)
        payload.saml_attribute_first_name =
          form.saml_attribute_first_name.trim();
      if (form.saml_attribute_last_name)
        payload.saml_attribute_last_name =
          form.saml_attribute_last_name.trim();
    }
    save.mutate(payload);
  };

  if (config.isLoading && !config.isFetched) {
    return (
      <section className="card text-sm text-nb-text-2">
        {t("admin.sso.loading")}
      </section>
    );
  }

  const has = !!config.data;
  const hasSecret = !!config.data?.has_client_secret;
  const hasCert = !!config.data?.has_x509_cert;
  const isSaml = form.provider === "saml";

  return (
    <section className="card space-y-4">
      <header>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {isSaml ? t("admin.sso.cardTitleSaml") : t("admin.sso.cardTitleOidc")}
        </h2>
        <p className="text-xs text-nb-text-3">{t("admin.sso.cardHint")}</p>
      </header>

      {/* Provider selector — at the top so the form below reshapes
          when the admin flips between protocols. */}
      <div
        className="flex items-center gap-3"
        data-testid="sso-provider-toggle"
      >
        <span className="text-xs font-medium uppercase tracking-wide text-nb-text-2">
          {t("admin.sso.protocolLabel")}
        </span>
        <label className="flex items-center gap-1 text-sm text-nb-text">
          <input
            type="radio"
            name="sso-provider"
            value="oidc"
            checked={form.provider === "oidc"}
            onChange={() => setForm({ ...form, provider: "oidc" })}
          />
          {t("admin.sso.protocolOidc")}
        </label>
        <label className="flex items-center gap-1 text-sm text-nb-text">
          <input
            type="radio"
            name="sso-provider"
            value="saml"
            checked={form.provider === "saml"}
            onChange={() => setForm({ ...form, provider: "saml" })}
          />
          {t("admin.sso.protocolSaml")}
        </label>
      </div>

      <div className="rounded-md border border-nb-border bg-slate-900/40 px-3 py-2 text-xs">
        <div className="text-nb-text-2">{callbackLabel}</div>
        <code
          className="break-all text-nb-mint"
          data-testid="sso-callback-url"
        >
          {callbackUrl}
        </code>
      </div>

      <form className="space-y-3" onSubmit={onSubmit}>
        <div>
          <label className="label" htmlFor="display_name">
            {t("admin.sso.displayName")}
          </label>
          <input
            id="display_name"
            className="input"
            value={form.display_name}
            placeholder={
              isSaml
                ? t("admin.sso.displayNameSamlPlaceholder")
                : t("admin.sso.displayNameOidcPlaceholder")
            }
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
          />
        </div>

        {/* OIDC fields */}
        {!isSaml && (
          <div className="space-y-3" data-testid="oidc-fields">
            <div>
              <label className="label" htmlFor="issuer_url">
                {t("admin.sso.issuerUrl")}
              </label>
              <input
                id="issuer_url"
                className="input"
                type="url"
                value={form.issuer_url}
                placeholder={t("admin.sso.issuerUrlPlaceholder")}
                onChange={(e) => setForm({ ...form, issuer_url: e.target.value })}
                required
              />
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <button
                type="button"
                className="btn-ghost text-xs"
                onClick={() => testDiscovery.mutate()}
                disabled={!form.issuer_url || testDiscovery.isPending}
              >
                {testDiscovery.isPending
                  ? t("admin.sso.testDiscoveryProbing")
                  : t("admin.sso.testDiscovery")}
              </button>
              {probe && (
                <p
                  data-testid="probe-result"
                  className={`text-xs ${probe.ok ? "text-nb-text" : "text-cvp-danger"}`}
                >
                  {probe.ok
                    ? t(
                        probe.jwks_keys === 1
                          ? "admin.sso.probeOkOne"
                          : "admin.sso.probeOkOther",
                        { count: probe.jwks_keys },
                      )
                    : t("admin.sso.probeFailed", {
                        reason: probe.reason,
                        detail: probe.detail,
                      })}
                </p>
              )}
            </div>
            <div>
              <label className="label" htmlFor="client_id">
                {t("admin.sso.clientId")}
              </label>
              <input
                id="client_id"
                className="input"
                value={form.client_id}
                onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="label" htmlFor="client_secret">
                {t("admin.sso.clientSecret")}{" "}
                {hasSecret && (
                  <span className="text-xs text-nb-text-3">
                    {t("admin.sso.clientSecretStored")}
                  </span>
                )}
              </label>
              <PasswordInput
                id="client_secret"
                autoComplete="new-password"
                value={form.client_secret}
                placeholder={
                  hasSecret ? t("admin.sso.clientSecretStoredPlaceholder") : ""
                }
                onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
              />
            </div>
          </div>
        )}

        {/* SAML fields */}
        {isSaml && (
          <div className="space-y-3" data-testid="saml-fields">
            {/* Phase 3-saml-metadata — file picker. Drops a metadata.xml
                and auto-fills entity ID + SSO URL + cert from the
                parsed response. Admin still has to click Save to
                persist. Pure UX accelerator — manual entry below stays
                fully functional for IdPs that don't publish metadata. */}
            <div className="rounded-md border border-dashed border-nb-border bg-slate-900/20 p-3">
              <div className="text-sm font-medium text-nb-text">
                Import IdP metadata.xml
              </div>
              <p className="mt-1 text-xs text-nb-text-3">
                Optional. Drag in your IdP&apos;s published metadata file
                to auto-fill the SAML fields below. The platform parses +
                returns a preview; nothing is persisted until you click
                Save.
              </p>
              <input
                id="saml-metadata-file"
                type="file"
                accept=".xml,application/xml,text/xml"
                className="mt-2 block text-xs"
                data-testid="saml-metadata-file"
                disabled={importMetadata.isPending}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setMetadataMessage(t("admin.sso.metadataParsing"));
                  // file.text() exists in modern browsers but jsdom
                  // doesn't ship it — read via FileReader so tests pass.
                  const xml = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(String(reader.result || ""));
                    reader.onerror = () => reject(reader.error);
                    reader.readAsText(file);
                  });
                  importMetadata.mutate(xml);
                  // Allow re-picking the same file later.
                  e.target.value = "";
                }}
              />
              {metadataMessage && (
                <div
                  className={`mt-2 text-xs ${
                    metadataMessage.startsWith("Loaded")
                      ? "text-nb-ok"
                      : metadataMessage.startsWith("Parsing")
                        ? "text-nb-mute"
                        : "text-nb-danger"
                  }`}
                  data-testid="saml-metadata-message"
                  role="status"
                >
                  {metadataMessage}
                </div>
              )}
            </div>
            <div>
              <label className="label" htmlFor="saml_entity_id">
                {t("admin.sso.entityId")}
              </label>
              <input
                id="saml_entity_id"
                className="input"
                value={form.saml_entity_id}
                placeholder={t("admin.sso.entityIdPlaceholder")}
                onChange={(e) =>
                  setForm({ ...form, saml_entity_id: e.target.value })
                }
                required
              />
            </div>
            <div>
              <label className="label" htmlFor="saml_sso_url">
                {t("admin.sso.ssoUrl")}
              </label>
              <input
                id="saml_sso_url"
                className="input"
                type="url"
                value={form.saml_sso_url}
                placeholder={t("admin.sso.ssoUrlPlaceholder")}
                onChange={(e) =>
                  setForm({ ...form, saml_sso_url: e.target.value })
                }
                required
              />
            </div>
            <div>
              <label className="label" htmlFor="saml_x509_cert">
                {t("admin.sso.certLabel")}{" "}
                {hasCert && (
                  <span className="text-xs text-nb-text-3">
                    {t("admin.sso.certStored")}
                  </span>
                )}
              </label>
              <textarea
                id="saml_x509_cert"
                className="input min-h-[120px] font-mono text-xs"
                value={form.saml_x509_cert}
                placeholder={
                  hasCert
                    ? t("admin.sso.certStoredPlaceholder")
                    : t("admin.sso.certPlaceholder")
                }
                onChange={(e) =>
                  setForm({ ...form, saml_x509_cert: e.target.value })
                }
              />
            </div>
            <details className="rounded-md border border-nb-border bg-slate-900/30 px-3 py-2 text-xs text-nb-text-2">
              <summary className="cursor-pointer text-nb-text">
                {t("admin.sso.advancedAttrMapping")}
              </summary>
              <div className="mt-3 space-y-2">
                <div>
                  <label className="label" htmlFor="saml_name_id_format">
                    {t("admin.sso.nameIdFormat")}
                  </label>
                  <input
                    id="saml_name_id_format"
                    className="input"
                    value={form.saml_name_id_format}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        saml_name_id_format: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="label" htmlFor="saml_attribute_email">
                    {t("admin.sso.attrEmail")}
                  </label>
                  <input
                    id="saml_attribute_email"
                    className="input"
                    value={form.saml_attribute_email}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        saml_attribute_email: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="label" htmlFor="saml_attribute_first_name">
                    {t("admin.sso.attrFirstName")}
                  </label>
                  <input
                    id="saml_attribute_first_name"
                    className="input"
                    value={form.saml_attribute_first_name}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        saml_attribute_first_name: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="label" htmlFor="saml_attribute_last_name">
                    {t("admin.sso.attrLastName")}
                  </label>
                  <input
                    id="saml_attribute_last_name"
                    className="input"
                    value={form.saml_attribute_last_name}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        saml_attribute_last_name: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </details>
          </div>
        )}

        {/* Shared fields */}
        <div>
          <label className="label" htmlFor="email_domains">
            {t("admin.sso.emailDomains")}
          </label>
          <input
            id="email_domains"
            className="input"
            value={form.email_domains}
            placeholder={t("admin.sso.emailDomainsPlaceholder")}
            onChange={(e) => setForm({ ...form, email_domains: e.target.value })}
          />
          <p className="mt-1 text-xs text-nb-text-3">
            {t("admin.sso.emailDomainsHint")}
          </p>
        </div>
        <div>
          <label className="label" htmlFor="default_role_slug">
            {t("admin.sso.defaultRole")}
          </label>
          <input
            id="default_role_slug"
            className="input"
            value={form.default_role_slug}
            placeholder={t("admin.sso.defaultRolePlaceholder")}
            onChange={(e) =>
              setForm({ ...form, default_role_slug: e.target.value })
            }
          />
        </div>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm text-nb-text">
            <input
              type="checkbox"
              checked={form.auto_provision_users}
              onChange={(e) =>
                setForm({ ...form, auto_provision_users: e.target.checked })
              }
            />
            {t("admin.sso.autoProvision")}
          </label>
          <label className="flex items-center gap-2 text-sm text-nb-text">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            />
            {t("admin.sso.enabledLabel")}
          </label>
        </div>

        {save.isError && (
          <p className="text-xs text-cvp-danger" role="alert">
            {(() => {
              const err = save.error as unknown as {
                response?: { data?: unknown };
              };
              return err?.response?.data
                ? JSON.stringify(err.response.data)
                : t("admin.sso.saveErrorGeneric");
            })()}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <button
            type="submit"
            className="btn-primary text-sm"
            disabled={save.isPending}
          >
            {save.isPending
              ? t("admin.sso.saving")
              : has
                ? t("admin.sso.saveButton")
                : t("admin.sso.createButton")}
          </button>
          {has && (
            <button
              type="button"
              className="btn-danger text-sm"
              onClick={() => {
                if (window.confirm(t("admin.sso.removeConfirm"))) {
                  remove.mutate();
                }
              }}
              disabled={remove.isPending}
            >
              {remove.isPending ? t("admin.sso.removing") : t("admin.sso.removeButton")}
            </button>
          )}
        </div>
      </form>
    </section>
  );
}
